import { type FC } from "react";
import {
  Checkbox,
  FormControlLabel,
  Link as MuiLink,
  Typography,
} from "@mui/material";
import { useTranslation, usePlatform } from "@/shared/hooks";
import { isExternalUrl } from "@/shared/utils";
import {
  getPaymentTermsUrls,
  isPaymentTermsRequired,
} from "./paymentTermsUrls";

// Порт arena/components/ProductPayment/PaymentTerms.js: ссылки на условия
// оферты/возврата платформы сразу после caption периода доступа. Рендерится
// только если оба url заданы (референс: `if (!termsOfServiceUrl) return null`,
// аналогично refundPolicyUrl) — эти же поля платформы уже используются в
// Footer.tsx:110-137. Нет ветки DONATION (текст productPayment.acceptDonation
// референса) — ProductType сайта ограничен PPV|SUBSCRIPTION.
//
// Task 11 (Paynet compliance): требование интеграционного пакета Paynet —
// покупатель обязан явно поставить галочку о прочтении условий перед оплатой,
// пассивного текста недостаточно. Текст со ссылками остался тем же, только
// обёрнут в Checkbox/FormControlLabel (тот же паттерн, что и согласие в
// LandingScreen/ContactForm.tsx:265-289). Если цена не в MDL (то есть платёж
// уйдёт не в Paynet), галочки нет, остаётся только текст со ссылками, как в
// референсе, и ProductPayment не блокирует кнопку оплаты (см.
// isPaymentTermsRequired). Без обоих url платформы компонент возвращает null.

export const PaymentTerms: FC<{
  currency: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  /** Показывать независимо от валюты: шаг Stripe в PaymentDialog спрашивает
   * согласие у всех, там валюта уже не важна (нужны только оба url). */
  always?: boolean;
}> = ({ currency, checked, onChange, always = false }) => {
  const { t } = useTranslation();
  const { platform } = usePlatform();

  const urls = getPaymentTermsUrls(platform);

  if (!urls) return null;

  const showCheckbox = always || isPaymentTermsRequired(platform, currency);

  const text = (
    <Typography color="text.secondary" variant="caption" display="inline">
      {/* Без галочки формулировка про галочку врёт: согласие в этом случае
      даёт сама кнопка оплаты. */}
      {t(showCheckbox ? "productPayment.accept" : "productPayment.acceptOnBuy")}{" "}
      <MuiLink
        href={urls.termsOfServiceUrl}
        color="text.primary"
        {...(isExternalUrl(urls.termsOfServiceUrl)
          ? { target: "_blank", rel: "noopener noreferrer" }
          : {})}
        underline="hover"
      >
        {t("productPayment.termsOfService")}
      </MuiLink>{" "}
      {t("productPayment.and").trim()}{" "}
      <MuiLink
        href={urls.refundPolicyUrl}
        color="text.primary"
        {...(isExternalUrl(urls.refundPolicyUrl)
          ? { target: "_blank", rel: "noopener noreferrer" }
          : {})}
        underline="hover"
      >
        {t("productPayment.refundPolicy")}
      </MuiLink>
      {t("productPayment.finish")}
    </Typography>
  );

  // Референс PaymentTerms.js:10-11: текст со ссылками показывается при любой
  // валюте, как только у платформы заданы оба url. Галочка — наша надстройка
  // для Paynet (см. ниже), поэтому валюта решает только её наличие, а не
  // наличие самих ссылок.
  if (!showCheckbox) return text;

  return (
    <FormControlLabel
      control={
        <Checkbox
          checked={checked}
          onChange={(event) => onChange(event.target.checked)}
          size="small"
          sx={{ color: "text.secondary" }}
        />
      }
      label={text}
      sx={{ alignItems: "center", ml: 0, gap: 0.5 }}
    />
  );
};
